import {
	OrderAction,
	OrderPaymentStatus,
	useCancelOrderMutation,
	useGetOrderQuery,
	useGetOrderTransactionsQuery,
	useGetQuoteOfferLinesQuery,
	useUpdateOrderQuoteMutation,
} from '@codezo/graphql';
import {
	OrderHeader,
	OrderOverview,
	OrderProducts,
	OrderDeliveries,
	OrderTotal,
	buildProgressRows,
	clientMeta,
	type OrderPaymentRow,
	type OrderProductFact,
	type OrderProductRow,
	type OrderTotalProps,
	type SummaryLine,
	type Tone,
} from '@codezo/order-detail';
import { FileGrid, FileTile, SectionHead, fileMeta } from '@codezo/quote-detail';
import { buildSpecificationRows } from '@codezo/product-specs';
import { ArrowUpRight, ArrowLeft, MoreHorizontal, Truck } from 'lucide-react';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetPageMeta } from '../../../hooks/useSetPageMeta';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { CurrencyText } from '../../../components/CurrencyText';
import { ProductImage, orderedProductImages } from '../../../components/ui/product-image';
import { getMutationError } from '../../../lib/mutation-response';
import { inSortOrder } from './derived';
import { ProductCommentsButton } from '../../../components/quotes/threads/ProductCommentsButton';
import { ProductThreadsPanel } from '../../../components/quotes/threads/ProductThreadsPanel';
import { useQuoteThreads } from '../../../components/quotes/threads/useQuoteThreads';
import { OrderFlowSection } from './OrderFlowSection';
import { OrderPaymentSettings } from './OrderPaymentSettings';
import { OrderTransactionsSection } from './OrderTransactionsSection';
import { toDeliveryRows } from './deliveries';
import { ShipmentActionButtons } from './ShipmentActions';
import { UpdateShipmentTrackingModal } from '../../../components/orders/UpdateShipmentTrackingModal';
import { ItemEscapeActions } from './ItemActions';
import { FilePreview, type FilePreviewItem } from '../../../components/ui/file-preview';
import { OrderActivityTimeline } from '../../../components/orders/OrderActivityTimeline';
import { RecordShipmentModal } from '../../../components/orders/RecordShipmentModal';
import { formatDate } from '../../../lib/utils';
import { ORDER_STATUS } from '../../../lib/status-configs';
// The admin side of the packages' "money arrives pre-formatted" contract. It
// lives under the quote page because that page needed it first; it is not
// quote-specific and would sit better in `lib/`.
import { useMoney } from '../../quotes/v2/format';
import { OrderDetailsCard } from './OrderDetailsCard';
import { EditOrderDeliveryAddressModal } from '../../../components/orders/EditOrderDeliveryAddressModal';
import { useCurrencyStore } from '../../../stores/currency';

/**
 * Order detail, v2 — the Flow C design.
 *
 * OPT-IN, AND V1 IS UNTOUCHED. This page lives at `/orders/:id/v2`. The default
 * `/orders/:id` is still `OrderDetailPageNew` and `/orders/:id/old` is still the
 * original, and neither changes until this has been through QA. Same shape the
 * quote page used to cross over.
 *
 * WHERE THE PIECES COME FROM. Everything drawn here is meant to end up in
 * `@codezo/order-detail`, so the client app can render the same page with
 * `role="client"`. What stays behind in this file is what only an app can own:
 * the query, the mutations, the modals, and the buttons that open them.
 */

/** Display tones for the payment statuses. An enum, so exhaustive. */
const PAYMENT_STATUS: Record<OrderPaymentStatus, { label: string; tone: Tone }> = {
	[OrderPaymentStatus.Awaiting]: { label: 'Awaiting', tone: 'warn' },
	[OrderPaymentStatus.Partial]: { label: 'Part paid', tone: 'info' },
	[OrderPaymentStatus.Settled]: { label: 'Settled', tone: 'success' },
	[OrderPaymentStatus.Waived]: { label: 'Waived', tone: 'neutral' },
	[OrderPaymentStatus.NotApplicable]: { label: 'None', tone: 'neutral' },
};

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-5 h-4 w-16 animate-pulse rounded bg-surface-fill" />
			<div className="mb-7">
				<div className="mb-2 h-7 w-64 animate-pulse rounded bg-surface-fill" />
				<div className="h-4 w-96 animate-pulse rounded bg-surface-fill" />
			</div>
			<div className="mb-7 grid grid-cols-4 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken">
				{Array.from({ length: 4 }).map((_, i) => (
					<div key={i} className="px-5 py-4">
						<div className="mb-2 h-3 w-16 animate-pulse rounded bg-surface-fill" />
						<div className="h-6 w-10 animate-pulse rounded bg-surface-fill" />
					</div>
				))}
			</div>
		</div>
	);
}

export function OrderDetailPageV2() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { selectedCurrency } = useCurrencyStore();

	const [recordingShipment, setRecordingShipment] = useState(false);
	/**
	 * The shipment whose details are being corrected, and which of its actions
	 * opened the form. Page-level because one modal serves both places a
	 * shipment's moves appear — the product flow and the deliveries list.
	 */
	const [tracking, setTracking] = useState<{ shipmentId: string; actionKey: string } | null>(
		null
	);
	const [cancelOpen, setCancelOpen] = useState(false);
	/** The file set currently open in the previewer, and where it starts. */
	const [preview, setPreview] = useState<{
		files: FilePreviewItem[];
		index: number;
	} | null>(null);
	const [cancelOrder, { loading: cancelling }] = useCancelOrderMutation();
	const [updateQuote, { loading: savingManagers }] = useUpdateOrderQuoteMutation();
	const [editManagersOpen, setEditManagersOpen] = useState(false);
	const [editAddressOpen, setEditAddressOpen] = useState(false);
	const money = useMoney();

	const { data, loading, networkStatus, refetch } = useGetOrderQuery({
		variables: { id: id!, inCurrency: selectedCurrency },
		skip: !id,
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	/* Who gets emailed is computed live by the contacts section, so it re-asks
	   whenever the order is refreshed — the same signal the quote page uses. */
	const [recipientsVersion, bumpRecipients] = useReducer((n: number) => n + 1, 0);
	const refreshOrder = useCallback(() => {
		void refetch();
		bumpRecipients();
	}, [refetch]);

	const isInitialLoading = loading && networkStatus === 1;
	const order = data?.getOrder;

	useSetPageMeta(order?.name || 'Order', [
		{ label: 'Orders', path: '/orders' },
		{ label: order?.name || 'Loading...' },
	]);

	// Memoised because the rows are rebuilt from two lists and the matrix is
	// rendered on every scroll tick of the sticky header above it.
	const progress = useMemo(
		() =>
			order
				? buildProgressRows({ items: order.items ?? [], shipments: order.shipments ?? [] })
				: [],
		[order]
	);

	/* Product threads carry over from the quote. They hang off the quote line,
	   which an order item only names through its offer — hence the lookup. */
	const quoteThreads = useQuoteThreads(order?.orderQuoteId ?? undefined);
	const { data: offerLineData } = useGetQuoteOfferLinesQuery({
		variables: { orderQuoteId: order?.orderQuoteId ?? '' },
		fetchPolicy: 'no-cache',
		skip: !order?.orderQuoteId,
	});
	const lineOfOffer = useMemo(
		() =>
			new Map(
				(offerLineData?.getOrderQuoteOfferBatches ?? []).flatMap((b) =>
					b.offers.map((o) => [o.id, o.orderQuoteItemId] as const)
				)
			),
		[offerLineData]
	);

	/* Invoices booked before the address was last corrected carry the previous
	   address as their ship-to and are never re-issued. Only asked for once the
	   address has actually been changed — until then every invoice matches. */
	const addressChangedAt = order?.deliveryAddressChangedAt ?? null;
	const { data: transactionsData } = useGetOrderTransactionsQuery({
		variables: { orderId: id! },
		fetchPolicy: 'no-cache',
		skip: !id || !addressChangedAt,
	});
	const invoicesWithPreviousAddress = useMemo(() => {
		if (!addressChangedAt) return [];
		const changedAt = new Date(addressChangedAt).getTime();
		return [
			...new Set(
				(transactionsData?.getOrderTransactionsV2 ?? [])
					.filter(
						(row) =>
							!row.isHistory &&
							row.invoiceNumber != null &&
							new Date(row.date).getTime() < changedAt
					)
					.map((row) => row.invoiceNumber!)
			),
		];
	}, [transactionsData, addressChangedAt]);

	if (isInitialLoading || !order) return <DetailSkeleton />;

	// Every figure below is read, not computed — see `derived.ts`.
	const items = inSortOrder(order.items ?? []);
	const summary = order.summary;
	const marginPct = summary?.marginPercentage;
	// What the client pays, VAT included. The old grossAmount ?? grandTotal ??
	// totalAmount chain is gone: those answer three different questions, and
	// totalAmount is deprecated.
	const total = summary?.grossAmount;
	const pendingConfirmation = order.itemsAwaitingConfirmation ?? 0;
	const statusConfig = ORDER_STATUS[order.status] ?? { label: order.status, tone: 'neutral' };

	// Wording, not arithmetic: "100 + 100 units" reads well for a couple of items
	// and turns into a wall past that, so it degrades to a count.
	const qtyHint =
		items.length > 1 && items.length <= 3
			? `${items.map((i) => i.quantity.toLocaleString()).join(' + ')} units`
			: `across ${items.length} ${items.length === 1 ? 'product' : 'products'}`;

	const payment = order.paymentSummary;

	// What the order totals, in the quote's own panel — so an order and the quote
	// it came from can't appear to add up differently. Money is pre-formatted
	// because that panel renders for the client too, where the currency differs.
	const selection: OrderTotalProps['selection'] = items.map((item) => ({
		id: item.id,
		kind: 'product' as const,
		name: item.productName || 'Product',
		sub: [
			`${item.quantity.toLocaleString()} pcs`,
			// The server's wording, like every other label on this page — the local
			// enum→text map is gone.
			item.delivery?.shippingMethodLabel ?? null,
		]
			.filter(Boolean)
			.join(' · '),
		total: money(item.lineTotal),
	}));

	// Grouped by the THING the money is about — the goods and shipping the goods,
	// then the sample and shipping the sample — not by the kind of charge. Putting
	// the two freight lines together instead leaves each away from what it ships.
	// Fees and discounts are listed individually when there are any: a client
	// asking "what is this DKK 400 fee" is asking about a line, not a total.
	/**
	 * Where the money stands. Every figure is a field — no column is derived from
	 * the other two, and the total line is the server's own rather than the two
	 * buckets added together, which its own docs explicitly warn against.
	 *
	 * A sample bucket reporting NOT_APPLICABLE is dropped rather than shown as
	 * zeros: "no samples were ordered" is not "nothing owed for samples yet".
	 */
	/**
	 * What has already been paid, as deductions from the order total.
	 *
	 * A bucket appears only once money has actually moved against it — an unpaid
	 * one is not a line here, it is what "Remaining to pay" is made of.
	 */
	/**
	 * Which products a paid bucket actually covers.
	 *
	 * `paymentSummary` splits money into two order-level buckets — every sample,
	 * and all production — so a row reading "Sample payment · DKK 875" on a
	 * two-product order says nothing about WHICH sample was paid for. On a
	 * part-paid bucket that is the first thing anyone asks.
	 *
	 * Read off `paidAt`, which the server sets on the thing that was paid: on the
	 * sample round for a sample invoice, on the item for a production one. Not a
	 * split of the amount — the figure stays the bucket's own — just the names of
	 * what is behind it.
	 */
	const covered = (paid: (item: (typeof items)[number]) => boolean) => {
		const names = items.filter(paid).map((item) => item.productName || 'Product');
		if (names.length === 0) return undefined;
		return names.length === items.length ? 'All products' : names.join(' · ');
	};

	const paidRows: OrderPaymentRow[] = payment
		? [
				...(payment.samplePaidAmount > 0
					? [
							{
								key: 'samples',
								label: 'Sample payment',
								status: PAYMENT_STATUS[payment.sampleStatus],
								amount: money(payment.samplePaidAmount),
								// Which samples. The credit note that used to sit here is
								// gone: the Samples line above already explains the
								// charge-and-credit in full, and saying it twice in one
								// card left the row with a caveat where its subject
								// should be.
								note: covered((item) => !!item.samples?.some((s) => s.paidAt)),
							},
						]
					: []),
				...(payment.productPaidAmount > 0
					? [
							{
								key: 'products',
								label: 'Production payment',
								status: PAYMENT_STATUS[payment.productStatus],
								amount: money(payment.productPaidAmount),
								note: covered((item) => !!item.paidAt),
							},
						]
					: []),
			]
		: [];

	const summaryLines: SummaryLine[] = summary
		? [
				{
					label: 'Products subtotal',
					value: money(summary.productsSubtotal),
					group: 'products',
				},
				{
					label: 'Product delivery',
					value: money(summary.deliveryTotal),
					group: 'products',
				},
				// A PREPAID sample is charged up front and credited back in full on
				// the production invoice, so it contributes NOTHING to order value —
				// which is why the API keeps it out of `samplesTotal` and this line
				// reads zero while "Pay for samples" asks for real money.
				//
				// It cannot lead with "charged up front": every sample is a
				// prepayment, PREPAID ones included, so that describes both while
				// naming one's amount. What tells them apart is the credit.
				{
					label: 'Samples',
					value: money(summary.samplesTotal),
					group: 'samples',
					// Names what is actually CHARGED, on the invoice's own basis.
					//
					// This line is net and reads zero — a prepaid sample is credited
					// back, so it carries no order value. `samplesPrepaidTotal` is the
					// credited goods, also net. The sample invoice is gross. Quote the
					// net figure here and the page shows 700 against a checkout asking
					// 875, one sample described by two numbers that differ by VAT with
					// nothing saying so. `sampleTotalAmount` is the charge itself, so
					// this note, the pay button and the invoice all say one number.
					//
					// It stays a note rather than its own row: credited back in full, a
					// row here would appear to add to a total it contributes nothing to.
					note:
						summary.samplesPrepaidTotal > 0 ? (
							<div className="text-2xs leading-snug text-text-4">
								{money(payment?.sampleTotalAmount ?? summary.samplesPrepaidTotal)}{' '}
								incl. VAT charged up front, then credited back on the production
								invoice — which is why this line is zero. Part of the total below,
								not extra. See Payments.
							</div>
						) : undefined,
				},
				{
					label: 'Sample delivery',
					value: money(summary.samplesShippingTotal),
					group: 'samples',
				},
				...(summary.fees.length > 0
					? summary.fees.map((fee) => ({
							label: fee.name,
							value: money(fee.amount),
							group: 'adjustments',
							emphasis: 'fee' as const,
						}))
					: [{ label: 'Fees', value: null, group: 'adjustments' }]),
				...(summary.discounts.length > 0
					? summary.discounts.map((discount) => ({
							label: discount.name,
							value: money(discount.amount),
							group: 'adjustments',
							emphasis: 'discount' as const,
						}))
					: [{ label: 'Discounts', value: null, group: 'adjustments' }]),
			]
		: [];

	// Whether anything can go in a box is the server's rule, not ours — it appears
	// once something has finished production and comes back blocked, with a
	// reason, when everything produced is already on a shipment. v1 still keeps a
	// local status map for this; v2 reads the action.
	const shipmentAction = (order.availableActions ?? []).find(
		(a) => a.action === OrderAction.RecordShipment
	);

	const products: OrderProductRow[] = items.map((item) => {
		const images = orderedProductImages(item.productImages);
		const attachments = item.attachments ?? [];

		// Product images carry no id or filename — only url, altText and caption —
		// so the previewer's identity and label are built from what there is.
		const imageFiles: FilePreviewItem[] = images.map((img, i) => ({
			id: img.url,
			url: img.url,
			name: img.altText || img.caption || `${item.productName || 'Product'} ${i + 1}`,
			mimeType: 'image/*',
		}));

		// Admin sees the internal figures; the client app will pass the subset it
		// is allowed to. Sample money is deliberately absent — a sample's charge
		// depends on its paymentTreatment (CHARGED / PREPAID / WAIVED), and one
		// number here would misstate every prepaid and waived round. The spine's
		// sample section owns that.
		const facts: OrderProductFact[] = [
			['Qty', `${item.quantity.toLocaleString()} units`],
			['Base', <CurrencyText key="base" amount={item.baseTotalPrice} />],
			['Margin', <CurrencyText key="margin" amount={item.marginAmount} />],
			['Unit', <CurrencyText key="unit" amount={item.unitPrice} unit />],
		];

		return {
			id: item.id,
			name: item.productName || 'Product',
			// Snapshotted from the accepted quote line. Null on custom lines that
			// never had a catalogue product.
			sku: item.productSku,
			description: item.productDescription,
			// Dimensions and weight are the product's own, not part of the spec
			// object — the builder puts them first, ahead of the specifications, the
			// same way the quote's requested-products card orders them.
			specs: buildSpecificationRows({
				specifications: item.productSpecifications,
				dimensions: item.productDimensions,
				weight: item.productWeight,
			}).map((row) => [row.label, row.value] as OrderProductFact),
			// Just the images — the frames, the thumb grid and the empty-state box
			// are the card's, so a product with no pictures still holds its column.
			media: images.map((img, i) => (
				<ProductImage
					key={img.url}
					image={img}
					alt={i === 0 ? item.productName || '' : ''}
					className="h-full w-full"
				/>
			)),
			onOpenImages: (index) => setPreview({ files: imageFiles, index }),
			// Cancelling lives here rather than in the flow spine. It is an escape
			// from the flow, not a move along it — the design has no cancel anywhere
			// in the flow — and v1 keeps it on the product card for the same reason.
			actions: (
				<span className="flex items-center gap-0.5">
					{lineOfOffer.has(item.orderQuoteOfferId) && (
						<ProductCommentsButton
							{...quoteThreads.summaryFor(lineOfOffer.get(item.orderQuoteOfferId)!)}
							onClick={() =>
								quoteThreads.open(lineOfOffer.get(item.orderQuoteOfferId)!)
							}
						/>
					)}
					<ItemEscapeActions item={item} onChanged={refetch} />
				</span>
			),
			note: item.productSpecificationsAt
				? `Specifications as frozen when this order was accepted ${formatDate(
						item.productSpecificationsAt
					)}`
				: undefined,
			files: attachments.length > 0 && (
				<FileGrid>
					{attachments.map((file, i) => (
						<FileTile
							key={file.id}
							name={file.name}
							meta={fileMeta(file.name, file.mimeType, file.size)}
							description={file.description}
							onOpen={() =>
								setPreview({
									files: attachments.map((f) => ({
										id: f.id,
										name: f.name,
										url: f.url,
										mimeType: f.mimeType,
										size: f.size,
									})),
									index: i,
								})
							}
						/>
					))}
				</FileGrid>
			),
			filesEmpty: 'No files on this product — they carry over from the quote.',
			facts,
			total: [
				'Line total',
				// Adjustments are part of what this line is worth, so the adjusted
				// figure is the one that matches the summary rail.
				<CurrencyText key="total" amount={item.lineTotal} />,
			],
			primaryAction: item.productId ? (
				<Button
					variant="outline"
					size="sm"
					onClick={() => navigate(`/products/${item.productId}`)}
				>
					Open product
					<ArrowUpRight size={14} data-icon="inline-end" />
				</Button>
			) : undefined,
		};
	});
	const cancelAction = (order.availableActions ?? []).find(
		(a) => a.action === OrderAction.CancelOrder
	);

	const quote = order.orderQuote;
	// The quote's purpose and deadline, as one line under its name.
	const quoteMeta =
		[
			quote?.deadline && `Deadline ${formatDate(quote.deadline)}`,
			quote?.purpose &&
				(quote.purposeOther || quote.purpose.replace(/_/g, ' ').toLowerCase()),
		]
			.filter(Boolean)
			.join(' · ') || null;

	// Managers live on the quote — the order's internal alerts resolve them from
	// there — so assigning here writes the quote, exactly as the quote page does.
	const handleSaveManagers = async (ids: string[]) => {
		if (!quote) return;
		try {
			const { data: result } = await updateQuote({
				variables: { id: quote.id, input: { adminUserIds: ids } },
			});
			const error = getMutationError(result?.updateOrderQuote);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success('Managers updated');
			setEditManagersOpen(false);
			refreshOrder();
		} catch {
			toast.error('Failed to update managers');
		}
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<button
				onClick={() => navigate(-1)}
				className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Orders
			</button>

			<OrderHeader
				name={order.name}
				status={{ label: statusConfig.label, tone: statusConfig.tone }}
				subtitle={
					<>
						{order.organization?.name && (
							<>
								{order.organization.name}
								<span className="px-1.5 text-text-4">&middot;</span>
							</>
						)}
						{order.orderNumber && (
							<>
								<span className="font-mono">{order.orderNumber}</span>
								<span className="px-1.5 text-text-4">&middot;</span>
							</>
						)}
						{order.createdAt && <>Created {formatDate(order.createdAt)}</>}
					</>
				}
				actions={
					<>
						{/* Disabled with the server's own reason rather than hidden —
						    "why can't I ship this" is the question, and an absent
						    button doesn't answer it. Rendered only when the action
						    exists at all, which is the server saying it never applies. */}
						{shipmentAction && (
							<Button
								variant="default"
								size="sm"
								disabled={!!shipmentAction.blocked}
								title={
									shipmentAction.blocked
										? (shipmentAction.blockedReason ?? undefined)
										: 'Record what is going in a box'
								}
								onClick={() => setRecordingShipment(true)}
							>
								<Truck size={14} data-icon="inline-start" />
								{shipmentAction.label}
								{shipmentAction.itemCount != null &&
									shipmentAction.itemCount > 0 && (
										<span className="font-mono text-2xs text-text-4">
											{shipmentAction.itemCount}
										</span>
									)}
							</Button>
						)}
						{/* Only the server's own moves. v1 carries an "Edit order" item
						    with no handler behind it and no edit surface to open —
						    a menu entry that does nothing is worse than no entry, so
						    the menu appears only when there is something in it. */}
						{cancelAction && (
							<DropdownMenu>
								<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
									<MoreHorizontal size={14} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										disabled={!!cancelAction.blocked}
										onClick={() => setCancelOpen(true)}
									>
										{cancelAction.label}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</>
				}
			/>

			<div className="mt-5 mb-7">
				<OrderOverview
					progress={progress}
					stats={[
						{
							label: 'Items',
							value: String(items.length),
							// Confirmation is an internal margin step that gates starting
							// production — not the flow's first blocking gate. An item can
							// be sampling and still be counted here, so this reads as a
							// standing figure rather than something overdue.
							hint:
								pendingConfirmation > 0
									? `${pendingConfirmation} still to cost`
									: 'all costed',
						},
						{
							label: 'Total qty',
							value: (order.totalQuantity ?? 0).toLocaleString(),
							hint: qtyHint,
						},
						{
							label: 'Total amount',
							// `summary.grossAmount` and nothing else. v1's
							// grossAmount ?? grandTotal ?? totalAmount ladder is gone:
							// those three answer different questions (gross, net, and
							// lines + delivery with fees omitted), so falling through
							// them silently changed what the figure meant.
							value: <CurrencyText amount={total} />,
							hint:
								order.summary?.grossAmount != null
									? 'incl. VAT · from accepted quote'
									: 'from accepted quote',
						},
						{
							label: 'Margin',
							value: (
								<CurrencyText amount={summary?.marginTotal ?? order.marginTotal} />
							),
							// "of goods", because the denominator is product revenue —
							// samples, freight, fees and discounts carry no supplier cost.
							// Deliberately worded apart from the quote's
							// `markupPercentage`, which is markup on cost: a 25% markup is
							// a 20% margin, and two figures labelled the same get reported
							// as a bug.
							hint:
								marginPct != null
									? `${marginPct.toFixed(1)}% of goods · internal`
									: 'internal',
						},
						{
							label: 'Payments',
							value: <CurrencyText amount={payment?.totalPaidAmount} />,
							// The denominator is back: `paymentSummary` is now the
							// invoice basis — gross, freight included — so this and the
							// order total finally answer the same question.
							hint: (
								<>
									of <CurrencyText amount={total} className="text-text-4" />{' '}
									invoiced
								</>
							),
						},
					]}
				/>
			</div>

			{/* ── The page proper: spine on the left, rail on the right ── */}
			<div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px] lg:items-start">
				<div className="flex min-w-0 flex-col gap-7">
					{/* The shipping row needs the order's RECORD_SHIPMENT action for an
					    item that is not on a box yet - it opens the same modal the
					    header button does, which is why the page hands it down rather
					    than the flow reaching for a second one. */}
					<OrderFlowSection
						order={order}
						onChanged={refetch}
						recordShipment={
							shipmentAction && {
								label: shipmentAction.label,
								blocked: shipmentAction.blocked,
								blockedReason: shipmentAction.blockedReason,
								onOpen: () => setRecordingShipment(true),
							}
						}
					/>
					<OrderProducts products={products} />
					<OrderTransactionsSection order={order} onOrderChanged={refetch} />
					<section>
						<SectionHead
							title="Payment settings"
							meta="how this order's money is collected"
						/>
						<OrderPaymentSettings
							orderId={order.id}
							settings={{
								productPaymentMode: order.productPaymentMode,
								samplePaymentMode: order.samplePaymentMode,
								stripeCheckoutLimit: order.stripeCheckoutLimit,
								invoiceMinimum: order.invoiceMinimum,
							}}
							onSaved={refetch}
						/>
					</section>
				</div>

				<div className="flex flex-col gap-5 lg:sticky lg:top-6">
					<OrderDetailsCard
						client={
							order.organization
								? {
										name: order.organization.name,
										meta: clientMeta(order.organization),
										onOpen: () =>
											navigate(`/clients/${order.organization!.id}`),
									}
								: null
						}
						quote={
							quote
								? {
										id: quote.id,
										name: quote.name,
										meta: quoteMeta,
										description: quote.description,
										onOpen: () => navigate(`/quotes/${quote.id}`),
									}
								: null
						}
						address={order.deliveryAddress}
						addressLockedReason={order.deliveryAddressLockedReason}
						addressMismatch={
							addressChangedAt && invoicesWithPreviousAddress.length > 0
								? {
										changedAt: formatDate(addressChangedAt),
										invoiceNumbers: invoicesWithPreviousAddress,
									}
								: null
						}
						onEditAddress={() => setEditAddressOpen(true)}
						managers={{
							people: quote?.adminUsers ?? [],
							selectedIds: quote?.adminUserIds ?? [],
							onSave: handleSaveManagers,
							saving: savingManagers,
							open: editManagersOpen,
							onOpenChange: setEditManagersOpen,
						}}
						contacts={{
							organizationId: order.organizationId,
							clientContactUserIds: quote?.clientContactUserIds ?? [],
							recipientsVersion,
							onChanged: refreshOrder,
						}}
					/>
					<OrderTotal
						reference={order.orderNumber}
						selection={selection}
						lines={summaryLines}
						subtotal={money(summary?.grandTotal)}
						vatLabel={`VAT (${summary?.vatRate ?? 0}%)`}
						vat={money(summary?.vatAmount)}
						total={money(total)}
						totalNote="incl. VAT"
						// Admin-only; the client app passes nothing and the row doesn't
						// render. Passed even at zero or a loss — an order earning
						// nothing is a fact, and one sold below cost is the single thing
						// here most worth seeing.
						margin={
							summary?.marginTotal != null
								? [
										money(summary.marginTotal),
										summary.marginPercentage != null
											? `${summary.marginPercentage.toFixed(1)}%`
											: null,
									]
										.filter(Boolean)
										.join(' · ')
								: null
						}
						payments={
							payment
								? {
										paid: paidRows,
										remaining: money(payment.totalOutstandingAmount),
										// Three fields put in a row, not a calculation.
										// Every version of this block without it drew the
										// same question: do these numbers add up?
										equation: `${money(payment.totalAmount)} order total − ${money(
											payment.totalPaidAmount
										)} paid = ${money(payment.totalOutstandingAmount)} remaining`,
									}
								: undefined
						}
						marginNegative={(summary?.marginTotal ?? 0) < 0}
					/>
					{/* Every movement this order owes, sample rounds and shipments
					    alike — the same list the client sees, with the shipping
					    method added. */}
					<OrderDeliveries
						deliveries={toDeliveryRows(order.deliveries ?? [], (shipmentId) => {
							/* `Order.deliveries` is a display projection and carries
							   no actions of its own — it flattens three kinds of
							   record into one timeline. A SHIPMENT row shares its id
							   with the shipment behind it, so the row is joined back
							   to that record and renders ITS action list. Nothing is
							   derived from status here: the server's list is the
							   whole truth about what can be done, EDIT_DETAILS
							   included. */
							const shipment = (order.shipments ?? []).find(
								(s) => s.id === shipmentId
							);
							if (!shipment?.availableActions?.length) return undefined;
							return (
								<ShipmentActionButtons
									actions={shipment.availableActions}
									onPick={(actionKey) => setTracking({ shipmentId, actionKey })}
								/>
							);
						})}
						emptyState="Nothing has been made or shipped yet."
					/>
					{/* Last in the rail. It is a LOG — read when something needs
					    explaining, not as part of answering "where is this order" —
					    and it is the tallest thing here, so anything under it would
					    be pushed off the fold.

					    v1's component, not a second one. It already brings its own
					    header and count, its own loading state, and a scroll cap of
					    `min(900px, 78vh)` sized for exactly this: a `sticky` rail
					    stops sticking once it outgrows the viewport. The placeholder
					    that stood here proposed five events behind a "show all" link,
					    which is a worse answer to the same problem and a second thing
					    to keep in step with the studio's other timeline. */}
					<OrderActivityTimeline orderId={order.id} />
				</div>
			</div>

			{/* Mounted only while open: it pre-fills from the items on first render,
			    and the items change every time a shipment is recorded. */}
			{recordingShipment && (
				<RecordShipmentModal
					open
					orderId={order.id}
					items={items}
					onClose={() => setRecordingShipment(false)}
					onSaved={refetch}
				/>
			)}

			{/* Mounted only while open, like the record-shipment modal above and for
			    the same reason: the form seeds its fields from the shipment on
			    first render, so a persistent instance would carry one shipment's
			    values into the next. That prefill is what keeps EDIT_DETAILS safe
			    — on an edit a null CLEARS the field rather than defaulting it, so
			    a form that blanked what it was not shown would wipe it. */}
			{tracking && (
				<UpdateShipmentTrackingModal
					shipment={
						(order.shipments ?? []).find((s) => s.id === tracking.shipmentId) ?? null
					}
					initialActionKey={tracking.actionKey}
					onClose={() => setTracking(null)}
					onSaved={refetch}
				/>
			)}

			{quoteThreads.openItemId && (
				<ProductThreadsPanel
					productName={
						items.find(
							(i) => lineOfOffer.get(i.orderQuoteOfferId) === quoteThreads.openItemId
						)?.productName ?? 'Product'
					}
					threads={quoteThreads.openThreads}
					activeThread={quoteThreads.activeThread}
					unreadOf={quoteThreads.unreadOf}
					onTabChange={quoteThreads.setActiveThread}
					onClose={quoteThreads.close}
					onRead={quoteThreads.markLocallyRead}
				/>
			)}

			<EditOrderDeliveryAddressModal
				open={editAddressOpen}
				onOpenChange={setEditAddressOpen}
				orderId={order.id}
				organizationId={order.organizationId}
				initialValue={order.deliveryAddress ?? null}
				onSuccess={refetch}
			/>

			{preview && (
				<FilePreview
					files={preview.files}
					initialIndex={preview.index}
					onClose={() => setPreview(null)}
				/>
			)}

			<ConfirmDialog
				open={cancelOpen}
				onOpenChange={setCancelOpen}
				title="Cancel order"
				description={`Are you sure you want to cancel "${order.name}"? This action cannot be undone.`}
				confirmLabel="Cancel order"
				loading={cancelling}
				onConfirm={async () => {
					try {
						// `cancelOrder` is a MessageResponse: a refused cancellation
						// comes back 200 with `success: false` and the rule that
						// refused it. Awaiting the call and toasting regardless told
						// the studio an order was cancelled when it was not, then
						// refetched the unchanged order underneath the message.
						const { data: result } = await cancelOrder({
							variables: { id: order.id },
						});
						const error = getMutationError(result?.cancelOrder);
						if (error) {
							toast.error(error);
							return;
						}
						toast.success('Order cancelled');
						setCancelOpen(false);
						refetch();
					} catch {
						toast.error('Failed to cancel order');
					}
				}}
			/>
		</div>
	);
}
